package com.exura.pages.planCreator.components.seriesViews.inputs

import androidx.compose.runtime.*
import com.exura.components.Icon
import com.exura.features.plandefinition.domain.model.series.HourOfDay
import com.exura.ui.*
import com.exura.ui.modifiers.insetHoverableItemStyle
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input

data class HourSelection(
    val hour: Int,
    val selectedMinutes: Set<Int> = setOf(0), // Default to :00
    val customMinutes: Set<Int> = emptySet()
)

@Composable
internal fun TimeOfDaySelector(
    selectedTimes: List<HourOfDay>,
    onSelectionChange: (List<HourOfDay>) -> Unit,
    mode: PlanCreatorInputMode,
    enabled: Boolean = true,
    modifier: Modifier = Modifier
) {
    // Convert HourOfDay list to internal state representation
    val hourSelections by remember(selectedTimes) {
        derivedStateOf {
            selectedTimes.groupBy { it.hour }.map { (hour, times) ->
                val minutes = times.map { it.minute }.toSet()
                val presetMinutes = minutes.filter { it in setOf(0, 10, 15, 20, 30, 40, 45, 50) }.toSet()
                val customMinutes = minutes.filter { it !in setOf(0, 10, 15, 20, 30, 40, 45, 50) }.toSet()
                HourSelection(hour, presetMinutes, customMinutes)
            }
        }
    }

    var expandedHour by remember { mutableStateOf<Int?>(null) }
    val isInteractive = enabled && mode == PlanCreatorInputMode.EDIT

    val hours = (8..22).toList()

    Box(
        modifier = modifier
            .fillMaxWidth()
            .borderRadius(12.px)
            .border {
                width(1.px)
                style(LineStyle.Solid)
                color(ExuraColors.GRAY)
            }
            .padding(16.px)
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(12.px),
            modifier = Modifier.fillMaxWidth(),
        ) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(8.px)
            ) {
                hours.chunked(5).forEach { hourRow ->
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(8.px),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        hourRow.forEach { hour ->
                            val selection = hourSelections.find { it.hour == hour }
                            val isSelected = selection != null
                            val isExpanded = expandedHour == hour

                            Box(modifier = Modifier.weight(1f).fillMaxWidth()) {
                                Column(Modifier.fillMaxWidth()) {
                                    // Hour button
                                    HourOption(
                                        hour = hour,
                                        isSelected = isSelected,
                                        isExpanded = isExpanded,
                                        isInteractive = isInteractive,
                                        onClick = {
                                            if (isInteractive) {
                                                handleHourClick(
                                                    hour = hour,
                                                    currentSelections = hourSelections,
                                                    expandedHour = expandedHour,
                                                    onExpandedHourChange = { expandedHour = it },
                                                    onSelectionChange = { newSelections ->
                                                        val newTimes = newSelections.flatMap { selection ->
                                                            (selection.selectedMinutes + selection.customMinutes).map { minute ->
                                                                HourOfDay(selection.hour, minute)
                                                            }
                                                        }
                                                        onSelectionChange(newTimes)
                                                    }
                                                )
                                            }
                                        }
                                    )

                                    // Minute options (expanded)
                                    if (isExpanded && selection != null) {
                                        MinuteOptions(
                                            hourSelection = selection,
                                            isInteractive = isInteractive,
                                            onMinuteToggle = { minute, isCustom ->
                                                if (isInteractive) {
                                                    val newSelections = updateMinuteSelection(
                                                        hourSelections,
                                                        hour,
                                                        minute,
                                                        isCustom
                                                    )
                                                    val newTimes = newSelections.flatMap { selection ->
                                                        (selection.selectedMinutes + selection.customMinutes).map { minute ->
                                                            HourOfDay(selection.hour, minute)
                                                        }
                                                    }
                                                    onSelectionChange(newTimes)
                                                }
                                            },
                                            onCustomMinuteAdd = { minute ->
                                                if (isInteractive) {
                                                    val newSelections = addCustomMinute(hourSelections, hour, minute)
                                                    val newTimes = newSelections.flatMap { selection ->
                                                        (selection.selectedMinutes + selection.customMinutes).map { minute ->
                                                            HourOfDay(selection.hour, minute)
                                                        }
                                                    }
                                                    onSelectionChange(newTimes)
                                                }
                                            }
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun HourOption(
    hour: Int,
    isSelected: Boolean,
    isExpanded: Boolean,
    isInteractive: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .height(48.px)
            .insetHoverableItemStyle(
                isSelected = isSelected,
                shadeColor = ExuraColors.BLUE.copy(alpha = 0.1),
                shadeSize = 4.px,
                hoverColor = if (isInteractive) ExuraColors.BLUE.copy(alpha = 0.05) else ExuraColors.TRANSPARENT,
                selectedColor = ExuraColors.BLUE.copy(alpha = 0.15),
                cursor = if (isInteractive) Cursor.Pointer else Cursor.Default,
                onClick = if (isInteractive) onClick else null,
                isHoverable = isInteractive
            )
            .borderRadius(if (isExpanded) 8.px else 24.px),
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            text = hour.toString().padStart(2, '0'),
            modifier = Modifier
                .classNames(TextStyles.normal14)
                .color(ExuraColors.ARSENIC)
        )
    }
}

@Composable
private fun MinuteOptions(
    hourSelection: HourSelection,
    isInteractive: Boolean,
    onMinuteToggle: (Int, Boolean) -> Unit,
    onCustomMinuteAdd: (Int) -> Unit,
    modifier: Modifier = Modifier
) {
    val presetMinutes = listOf(0, 10, 15, 20, 30, 40, 45, 50)
    var customMinuteInput by remember { mutableStateOf("") }

    Column(
        verticalArrangement = Arrangement.spacedBy(4.px),
        modifier = modifier.padding(top = 8.px).fillMaxWidth()
    ) {
        // Preset minute options in rows
        presetMinutes.chunked(4).forEach { minuteRow ->
            Row(
                horizontalArrangement = Arrangement.spacedBy(4.px),
                modifier = Modifier.fillMaxWidth()
            ) {
                minuteRow.forEach { minute ->
                    val isSelected = hourSelection.selectedMinutes.contains(minute)

                    Box(modifier = Modifier.weight(1f).fillMaxWidth()) {
                        MinuteOption(
                            minute = minute,
                            isSelected = isSelected,
                            isInteractive = isInteractive,
                            onClick = { onMinuteToggle(minute, false) }
                        )
                    }
                }

                // Fill remaining space if row is not complete
                repeat(4 - minuteRow.size) {
                    Box(modifier = Modifier.weight(1f).fillMaxWidth())
                }
            }
        }

        // Custom minutes row
        if (hourSelection.customMinutes.isNotEmpty() || isInteractive) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(4.px),
                modifier = Modifier.fillMaxWidth()
            ) {
                // Show existing custom minutes
                hourSelection.customMinutes.take(3).forEach { minute ->
                    Box(modifier = Modifier.weight(1f).fillMaxWidth()) {
                        MinuteOption(
                            minute = minute,
                            isSelected = true,
                            isCustom = true,
                            isInteractive = isInteractive,
                            onClick = { onMinuteToggle(minute, true) }
                        )
                    }
                }

                // Custom minute input
                if (isInteractive) {
                    Box(modifier = Modifier.weight(1f).fillMaxWidth()) {
                        CustomMinuteInput(
                            value = customMinuteInput,
                            onValueChange = { customMinuteInput = it },
                            onSubmit = { minute ->
                                if (minute in 0..59 && !hourSelection.selectedMinutes.contains(minute) && !hourSelection.customMinutes.contains(minute)) {
                                    onCustomMinuteAdd(minute)
                                    customMinuteInput = ""
                                }
                            },
                            isInteractive = isInteractive
                        )
                    }
                }

                // Fill remaining space
                val usedSlots = hourSelection.customMinutes.size + if (isInteractive) 1 else 0
                repeat(4 - usedSlots.coerceAtMost(4)) {
                    Box(modifier = Modifier.weight(1f))
                }
            }
        }
    }
}

@Composable
private fun MinuteOption(
    minute: Int,
    isSelected: Boolean,
    isCustom: Boolean = false,
    isInteractive: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .height(32.px)
            .insetHoverableItemStyle(
                isSelected = isSelected,
                shadeColor = if (isCustom) ExuraColors.ORANGE.copy(alpha = 0.1) else ExuraColors.BLUE.copy(alpha = 0.1),
                shadeSize = 4.px,
                hoverColor = if (isInteractive) ExuraColors.BLUE.copy(alpha = 0.05) else ExuraColors.TRANSPARENT,
                selectedColor = if (isCustom) ExuraColors.ORANGE.copy(alpha = 0.15) else ExuraColors.BLUE.copy(alpha = 0.15),
                cursor = if (isInteractive) Cursor.Pointer else Cursor.Default,
                onClick = if (isInteractive) onClick else null,
                isHoverable = isInteractive
            )
            .borderRadius(4.px),
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            text = ":${minute.toString().padStart(2, '0')}",
            modifier = Modifier
                .classNames(TextStyles.normal12)
                .color(ExuraColors.ARSENIC)
        )
    }
}

@Composable
private fun CustomMinuteInput(
    value: String,
    onValueChange: (String) -> Unit,
    onSubmit: (Int) -> Unit,
    isInteractive: Boolean,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .height(32.px)
            .border {
                width(1.px)
                style(LineStyle.Dashed)
                color(ExuraColors.GRAY)
            }
            .borderRadius(4.px),
        contentAlignment = Alignment.Center,
    ) {
        if (isInteractive) {
            Input(
                type = InputType.Number,
                attrs = Modifier
                    .fillMaxWidth()
                    .height(24.px)
                    .backgroundColor(Colors.Transparent)
                    .border(width = 0.px)
                    .outline(
                        width = 0.px,
                        style = LineStyle.None,
                        color = Colors.Transparent
                    )
                    .textAlign(TextAlign.Center)
                    .classNames(TextStyles.normal10)
                    .toAttrs {
                        value(value)
                        placeholder("MM")
                        onInput { onValueChange(it.value!!.toString()) }
                        onKeyDown { event ->
                            if (event.key == "Enter") {
                                value.toIntOrNull()?.let { minute ->
                                    onSubmit(minute)
                                }
                            }
                        }
                        onBlur {
                            value.toIntOrNull()?.let { minute ->
                                onSubmit(minute)
                            }
                        }
                    }
            )
        } else {
            Icon(
                iconSrc = Assets.IconsUntiled.ADD_TIME_OF_DAY,
                modifier = Modifier.size(16.px)
            )
        }
    }
}

// Helper functions for state management
private fun handleHourClick(
    hour: Int,
    currentSelections: List<HourSelection>,
    expandedHour: Int?,
    onExpandedHourChange: (Int?) -> Unit,
    onSelectionChange: (List<HourSelection>) -> Unit
) {
    val currentSelection = currentSelections.find { it.hour == hour }

    when {
        // Case: Clicking on already expanded hour
        expandedHour == hour -> {
            if (currentSelection != null) {
                // Deselect the hour
                onSelectionChange(currentSelections.filter { it.hour != hour })
                onExpandedHourChange(null)
            }
        }

        // Case: Clicking on different hour (expand it)
        currentSelection != null -> {
            onExpandedHourChange(hour)
        }

        // Case: Clicking on unselected hour (select and expand)
        else -> {
            val newSelection = HourSelection(hour, setOf(0)) // Auto-select :00
            onSelectionChange(currentSelections + newSelection)
            onExpandedHourChange(hour)
        }
    }
}

private fun updateMinuteSelection(
    currentSelections: List<HourSelection>,
    hour: Int,
    minute: Int,
    isCustom: Boolean
): List<HourSelection> {
    return currentSelections.map { selection ->
        if (selection.hour == hour) {
            if (isCustom) {
                selection.copy(customMinutes = selection.customMinutes - minute)
            } else {
                val newMinutes = if (selection.selectedMinutes.contains(minute)) {
                    selection.selectedMinutes - minute
                } else {
                    selection.selectedMinutes + minute
                }
                selection.copy(selectedMinutes = newMinutes)
            }
        } else {
            selection
        }
    }
}

private fun addCustomMinute(
    currentSelections: List<HourSelection>,
    hour: Int,
    minute: Int
): List<HourSelection> {
    return currentSelections.map { selection ->
        if (selection.hour == hour) {
            selection.copy(customMinutes = selection.customMinutes + minute)
        } else {
            selection
        }
    }
}
